import { Classes } from "@blueprintjs/core";
import classNames from "classnames";
import { CardStack } from "components/common/CardStack";
import { CustomUpgrade } from "in_queue/contexts/CustomUpgradesContext";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { CUSTOM_UPGRADE_STATUS_ARCHIVED } from "./customUpgradeConstants";
import {
    CustomUpgradePills,
    OriginPill,
    StatusPill
} from "./CustomUpgradePills";
import css from "./CustomUpgradesTable.module.scss";

export interface CustomUpgradeSelection {
    readonly selectedId: string | null;
    readonly onSelect: (upgrade: CustomUpgrade) => void;
    readonly selectableIds: ReadonlySet<string>;
}

type Interaction =
    | {
          readonly selection: CustomUpgradeSelection;
          readonly onRowClick?: never;
      }
    | {
          readonly onRowClick: (upgrade: CustomUpgrade) => void;
          readonly selection?: never;
      }
    | { readonly selection?: never; readonly onRowClick?: never };

type CustomUpgradesTableProps = {
    readonly upgrades: ReadonlyArray<CustomUpgrade>;
    readonly loading?: boolean;
    /** Grow with the rows until the flex parent runs out of height, then scroll. */
    readonly scrollable?: boolean;
} & Interaction;

const getFormatLabel = (upgrade: CustomUpgrade): string =>
    Array.from(
        new Set(upgrade.files.map((file) => `.${file.sourceFileFormat}`))
    ).join(" / ");

const isUsable = (upgrade: CustomUpgrade): boolean =>
    upgrade.modelSetValidationInfo.status.state === "usable";

// Archived upgrades sort to the bottom. Above them, shared upgrades come
// before custom ones, and validated (usable) upgrades lead each group.
const sortRank = (upgrade: CustomUpgrade): number => {
    if (upgrade.status === CUSTOM_UPGRADE_STATUS_ARCHIVED) return 4;
    const usableRank = isUsable(upgrade) ? 0 : 1;
    return upgrade.isShared ? usableRank : 2 + usableRank;
};

const UpgradeRow: React.FC<{
    readonly upgrade: CustomUpgrade;
    readonly selection?: CustomUpgradeSelection;
    readonly onRowClick?: (upgrade: CustomUpgrade) => void;
}> = ({ upgrade, selection, onRowClick }) => {
    const hasNotes = Boolean(upgrade.notes?.trim());
    const selectionMode = selection !== undefined;
    const rowSelectable =
        selection !== undefined && selection.selectableIds.has(upgrade.id);
    const isSelected =
        selection !== undefined && selection.selectedId === upgrade.id;
    const interactive = selectionMode
        ? rowSelectable
        : onRowClick !== undefined;

    const activate = () => {
        if (selection !== undefined) {
            if (rowSelectable) selection.onSelect(upgrade);
        } else if (onRowClick !== undefined) {
            onRowClick(upgrade);
        }
    };

    return (
        <div
            className={classNames(css["CustomUpgradesTable-row"], {
                [css["CustomUpgradesTable-row--interactive"]]: interactive,
                [css["CustomUpgradesTable-row--selected"]]: isSelected,
                [css["CustomUpgradesTable-row--disabled"]]:
                    selectionMode && !rowSelectable
            })}
            role={selectionMode ? "radio" : "button"}
            aria-checked={selectionMode ? isSelected : undefined}
            aria-disabled={selectionMode && !rowSelectable ? true : undefined}
            tabIndex={interactive ? 0 : -1}
            onClick={interactive ? activate : undefined}
            onKeyDown={
                interactive
                    ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              activate();
                          }
                      }
                    : undefined
            }
        >
            <div className={css["CustomUpgradesTable-main-cell"]}>
                {selectionMode && (
                    <span
                        className={classNames(
                            css["CustomUpgradesTable-radio"],
                            {
                                [css["CustomUpgradesTable-radio--selected"]]:
                                    isSelected,
                                [css["CustomUpgradesTable-radio--disabled"]]:
                                    !rowSelectable
                            }
                        )}
                        aria-hidden
                    />
                )}
                <div className={css["CustomUpgradesTable-main"]}>
                    <div className={css["CustomUpgradesTable-title-row"]}>
                        <span
                            className={classNames(
                                css["CustomUpgradesTable-title"],
                                Classes.TEXT_LARGE
                            )}
                        >
                            {upgrade.name}
                        </span>
                        <CustomUpgradePills upgrade={upgrade} />
                    </div>
                    <div
                        className={classNames(
                            css["CustomUpgradesTable-notes"],
                            {
                                [css["CustomUpgradesTable-notes--empty"]]:
                                    !hasNotes
                            }
                        )}
                    >
                        {hasNotes ? upgrade.notes : "No notes provided"}
                    </div>
                </div>
            </div>
            <div>
                <OriginPill upgrade={upgrade} />
            </div>
            <div className={css["CustomUpgradesTable-meta"]}>
                <div
                    className={classNames(
                        css["CustomUpgradesTable-format"],
                        Classes.MONOSPACE_TEXT
                    )}
                >
                    {getFormatLabel(upgrade)}
                </div>
                <div>
                    {DateTime.fromJSDate(upgrade.createdAt).toLocaleString(
                        DateTime.DATE_FULL
                    )}
                </div>
            </div>
            <div className={css["CustomUpgradesTable-status"]}>
                {upgrade.modelSetValidationInfo.status.state !==
                    "noModelSet" && (
                    <StatusPill
                        status={upgrade.modelSetValidationInfo.status}
                    />
                )}
            </div>
        </div>
    );
};

const SkeletonRow: React.FC = () => (
    <div className={css["CustomUpgradesTable-row"]}>
        <div className={css["CustomUpgradesTable-main-cell"]}>
            <div className={css["CustomUpgradesTable-main"]}>
                <span
                    className={classNames(
                        css["CustomUpgradesTable-title"],
                        Classes.TEXT_LARGE,
                        Classes.SKELETON
                    )}
                >
                    Loading upgrade name
                </span>
                <div
                    className={classNames(
                        css["CustomUpgradesTable-notes"],
                        Classes.SKELETON
                    )}
                >
                    Loading upgrade notes
                </div>
            </div>
        </div>
    </div>
);

export const CustomUpgradesTable: React.FC<CustomUpgradesTableProps> = ({
    upgrades,
    loading = false,
    scrollable = false,
    selection,
    onRowClick
}) => {
    const sortedUpgrades = useMemo(
        () =>
            [...upgrades].sort((a, b) => {
                const rankDiff = sortRank(a) - sortRank(b);
                if (rankDiff !== 0) return rankDiff;
                return b.createdAt.getTime() - a.createdAt.getTime();
            }),
        [upgrades]
    );

    return (
        <CardStack
            tone="white"
            columns
            className={classNames(css["CustomUpgradesTable"], {
                [css["CustomUpgradesTable--scrollable"]]: scrollable
            })}
        >
            {loading ? (
                [0, 1, 2].map((i) => <SkeletonRow key={i} />)
            ) : sortedUpgrades.length === 0 ? (
                <div className={css["CustomUpgradesTable-empty"]}>
                    No custom upgrades yet.
                </div>
            ) : (
                sortedUpgrades.map((upgrade) => (
                    <UpgradeRow
                        key={upgrade.id}
                        upgrade={upgrade}
                        selection={selection}
                        onRowClick={onRowClick}
                    />
                ))
            )}
        </CardStack>
    );
};
